import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useBilling, BillingStatus, QuotaSummary } from '../hooks/useBilling';
import { useAuth } from '../hooks/useAuth';
import { fetchWithInterceptors } from '../utils/apiInterceptors';

interface BillingContextType {
  billingStatus: BillingStatus | null;
  quota: QuotaSummary | undefined;
  loading: boolean;
  error: string | null;
  planName: string | null;
  planStatus: string | null;
  isTrialing: boolean;
  canCalculateQuote: boolean;
  refreshBilling: () => void;
  consumeQuota: () => Promise<boolean>;
  checkQuotaAvailable: () => boolean;
}

const BillingContext = createContext<BillingContextType | undefined>(undefined);

const logInDev = (level: 'warn' | 'error', ...args: unknown[]) => {
  if (import.meta.env.DEV) {
    console[level](...args);
  }
};

export const useBillingContext = () => {
  const context = useContext(BillingContext);
  if (!context) {
    throw new Error('useBillingContext must be used within a BillingProvider');
  }
  return context;
};

interface BillingProviderProps {
  children: React.ReactNode;
}

export const BillingProvider: React.FC<BillingProviderProps> = ({ children }) => {
  const { user } = useAuth();
  
  // BILLING DISABLED: All users have enterprise unlimited plans
  const billingDisabled = true;
  
  const { billingStatus: originalBillingStatus, loading: originalLoading, error: originalError, refreshBillingStatus } = useBilling();
  
  // Local state to track quota consumption in real-time
  const [localQuotaUsed, setLocalQuotaUsed] = useState(0);
  const [billingStatus, setBillingStatus] = useState<BillingStatus | null>(null);
  
  useEffect(() => {
    if (originalBillingStatus) {
      setBillingStatus(originalBillingStatus);
      setLocalQuotaUsed(0); // Reset local tracking when billing status refreshes
    }
  }, [originalBillingStatus]);

  // Calculate effective quota with local updates
  const quota: QuotaSummary | undefined = billingStatus?.quota ? {
    ...billingStatus.quota,
    used: (billingStatus.quota.used || 0) + localQuotaUsed,
    remaining: billingStatus.quota.unlimited 
      ? -1 
      : Math.max(0, (billingStatus.quota.remaining || 0) - localQuotaUsed)
  } : undefined;

  // Guard: if backend quota missing, synthesize from plan/trial to avoid showing 10/10 defaults
  const synthesizedQuota: QuotaSummary | undefined = quota ?? (() => {
    const planLimit = billingStatus?.plan?.maxQuotesMonthly
      ?? billingStatus?.plan?.max_quotes_per_month
      ?? 0;
    const trialLimit = billingStatus?.trial_info?.active ? (billingStatus?.quota?.trial_limit ?? 30) : undefined;
    const limit = (billingStatus?.trial_info?.active ? (trialLimit ?? 30) : planLimit) || 0;
    if (limit <= 0) return undefined;
    const used = billingStatus?.usage_count ?? 0;
    return {
      used,
      remaining: Math.max(0, limit - used),
      limit,
      unlimited: limit === -1,
      trial_limit: billingStatus?.trial_info?.active ? limit : null
    };
  })();

  const planName =
    billingStatus?.plan?.display_name
    || billingStatus?.plan?.displayName
    || (billingStatus?.has_subscription ? (billingStatus.plan?.plan_name ?? billingStatus.plan?.name ?? 'Active Plan') : (billingStatus?.trial_info ? 'Trial' : null));
  
  // Normalize status label to 'trial' for UX (avoid 'trialing'/'trailing')
  const rawStatus = billingStatus?.subscription?.status
    || (billingStatus?.has_subscription ? 'active' : (billingStatus?.trial_info ? 'trial' : null));
  const planStatus = rawStatus === 'trialing' ? 'trial' : rawStatus;

  const isTrialing = billingStatus?.trial_info?.active || planStatus === 'trial';

  // Check if user can calculate a quote based on their plan limits
  const checkQuotaAvailable = useCallback(() => {
    if (!quota) return false;
    if (quota.unlimited) return true;
    return quota.remaining > 0;
  }, [quota]);

  const canCalculateQuote = checkQuotaAvailable();

  // Consume a quota when calculating
  const consumeQuota = useCallback(async (): Promise<boolean> => {
    // BILLING DISABLED: Always return true (unlimited)
    if (billingDisabled) {
      return true;
    }
    
    if (!user?.company?.id && !user?.companyId) return false;
    
    // Check if quota is available
    if (!checkQuotaAvailable()) {
      return false;
    }

    try {
      // Track quota consumption on backend
      const response = await fetchWithInterceptors('/api/v1/billing/consume-quota', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          action: 'quote_calculation',
          count: 1 
        })
      });

      if (!response.ok) {
        // If backend fails, still update locally for immediate feedback
        logInDev('error', 'Failed to track quota consumption on backend');
      } else {
        const data = await response.json();
        if (data.success && data.data?.quota) {
          // Update billing status with fresh quota from backend
          setBillingStatus(prev => prev ? {
            ...prev,
            quota: data.data.quota,
            usage_count: data.data.quota.used,
            remaining: data.data.quota.remaining
          } : null);
          setLocalQuotaUsed(0); // Reset local tracking since we have fresh data
          return true;
        }
      }

      // Update local quota tracking for immediate UI feedback
      setLocalQuotaUsed(prev => prev + 1);
      return true;
    } catch (error) {
      logInDev('error', 'Error consuming quota:', error);
      // Still update locally for immediate feedback even if backend fails
      setLocalQuotaUsed(prev => prev + 1);
      return true;
    }
  }, [user?.company?.id, user?.companyId, checkQuotaAvailable]);

  // Refresh billing status (includes quota)
  const refreshBilling = useCallback(() => {
    setLocalQuotaUsed(0); // Reset local tracking
    refreshBillingStatus();
  }, [refreshBillingStatus]);

  const value: BillingContextType = useMemo(() => ({
    billingStatus,
    quota: synthesizedQuota,
    loading: originalLoading,
    error: originalError,
    planName,
    planStatus,
    isTrialing,
    canCalculateQuote,
    refreshBilling,
    consumeQuota,
    checkQuotaAvailable
  }), [
    billingStatus,
    synthesizedQuota,
    originalLoading,
    originalError,
    planName,
    planStatus,
    isTrialing,
    canCalculateQuote,
    refreshBilling,
    consumeQuota,
    checkQuotaAvailable
  ]);

  return (
    <BillingContext.Provider value={value}>
      {children}
    </BillingContext.Provider>
  );
};
